import React from 'react'
import { DateCell, PriceCell, RawTextCell, TooltipCell } from '@/components/Table/components/Cell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import {
  OfferQuotationDetailInstallAndSupplyUnit,
  OfferQuotationDetailMaintenanceUnit,
  PRStatus,
  TenderDocumentStatus,
} from '@eapproval/offer-shared'
import { SourcingDecisionWorkflowStatus } from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { MySourcingOfferDisplayLevel } from '@/features/tableProfiles/constants/tableProfile.constant'
import { camelToWords, formatCurrencyAmount, numberWithCommas } from '@/utils/stringHelper'
import { CostBudgetTypeLabel, Currency, OfferBookingStatus, OfferQuotationStatus } from '@eapproval/offer-shared'
import { OfferControlStatus } from '../../constants'
import { createColumnHelper } from '@tanstack/react-table'

const columnHelper = createColumnHelper()

export const getMySourcingTableColumns = (
  { sourcingUserOptions = [], vendorOptions = [], creatorOptions = [], salesUserOptions = [], closeReasons = [] },
  displayLevel = MySourcingOfferDisplayLevel.COST_BUDGET
) => {
  const baseColumns = [
    columnHelper.accessor('projectName', {
      size: 200,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('customer', {
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('salesResponsible', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: salesUserOptions,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('creator', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: creatorOptions,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('updatedAt', {
      header: () => <span>Last Update</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('status', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values({ ...OfferQuotationStatus, ...OfferBookingStatus, ...OfferControlStatus })
          .concat(closeReasons)
          .map((status) => ({
            label: camelToWords(status),
            value: status,
          })),
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetRfqNumber', {
      header: () => <span>RFQ #</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetSubSystemName', {
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetScopeOfSupply', {
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('sourcingDueDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('procurementResponsible', {
      header: () => <span>Procurement Responsible</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: sourcingUserOptions,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetProcurementCommented', {
      header: () => <span>Procurement Commented</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetNumberOfContractorPosted', {
      header: () => <span># of Contractor Posted</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetNumberOfUpload', {
      header: () => <span># of Contractor Uploaded</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetNumberOfPRSubmission', {
      header: () => <span># of PR Submission</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('costBudgetPM040SourcingSelection', {
      header: () => <span>PM040 Sourcing Selection</span>,
      cell: ({ getValue }) => {
        const value = getValue()
        return <TooltipCell text={value ? value.replace(/^(selected|reference)-/, '') : value} />
      },
      meta: {
        filterVariant: FilterType.SELECT,

        filterOptions: [
          { label: 'Selected', value: 'selected' },
          { label: 'Not Selected', value: '(-)' },
          { label: 'Reference Only', value: 'reference' },
          ...vendorOptions,
        ],
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetPM040TechnicalSelection', {
      header: () => <span>PM040 Technical Selection</span>,
      cell: ({ getValue }) => {
        const value = getValue()
        return <TooltipCell text={camelToWords(value ? value.replace(/^(selected|reference)-/, '') : value)} />
      },

      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Selected', value: 'selected' },
          { label: 'Not Selected', value: '(-)' },
          { label: 'Reference Only', value: 'reference' },

          ...vendorOptions,
        ],
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('costBudgetPM100TechnicalSelection', {
      header: () => <span>PM100 Technical Selection</span>,
      cell: ({ getValue }) => {
        const value = getValue()
        return <TooltipCell text={camelToWords(value ? value.replace(/^(selected|reference)-/, '') : value)} />
      },

      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Selected', value: 'selected' },
          { label: 'Not Selected', value: '(-)' },
          { label: 'Reference Only', value: 'reference' },

          ...vendorOptions,
        ],
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetQuoteAmount', {
      header: () => <span>Latest Quote Amount</span>,
      cell: ({ getValue, row: { original } }) => {
        const value = getValue()
        const tooltip = original?.lastUploadVendor ? `Last uploaded by ${original?.lastUploadVendor}` : ''
        return (
          <TooltipCell
            text={formatCurrencyAmount(value ?? 0, original?.costBudgetLatestQuoteCurrency ?? '')}
            tooltip={tooltip}
          />
        )
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetNoticedProcurementOfficer', {
      header: () => <span>Noticed Procurement Officer</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetRequireProcurementOfficer', {
      header: () => <span>Required Procurement Officer</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('costBudgetInternalRemark', {
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetIsActive', {
      header: () => <span>Cost Budget Is Active</span>,
      cell: ({ getValue }) => <TooltipCell text={getValue() === 'yes' ? 'Yes' : 'No'} />,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetType', {
      header: () => <span>Cost Budget Type</span>,
      cell: ({ getValue }) => <TooltipCell text={CostBudgetTypeLabel[getValue()] || getValue() || ''} />,
      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: Object.entries(CostBudgetTypeLabel).map(([value, label]) => ({ label, value })),
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetLatestQuoteCurrency', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(Currency).map((currency) => ({
          label: currency,
          value: currency,
        })),
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('costBudgetLargerThanThreshold', {
      header: () => <span>Cost Budget {'>'}100K</span>,

      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetWorkflowStatus', {
      header: () => <span>Workflow Status</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(SourcingDecisionWorkflowStatus).map((status) => ({
          label: status,
          value: status,
        })),
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetAddendumStatus', {
      header: () => <span>Addendum Status</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(TenderDocumentStatus).map((status) => ({
          label: status,
          value: status,
        })),
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetCTG', {
      header: () => <span>CTG</span>,
      cell: RawTextCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetCNI', {
      header: () => <span>CNI</span>,
      cell: RawTextCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetAppliedSingleSource', {
      header: () => <span>Apply Single Source Procurement ? </span>,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
  ]

  const costBudgetRfqColumns = [
    columnHelper.accessor('costBudgetRfqVendorName', {
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqQuotationUploader', {
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqQuotationAmount', {
      cell: ({ getValue, row: { original } }) => {
        const value = getValue()
        return <TooltipCell text={`$${numberWithCommas(getValue())}`} />
      },
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),

    columnHelper.accessor('costBudgetRfqValidDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqSubmitDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),

    // columnHelper.accessor('costBudgetRfqCurrency', {
    //   cell: RawTextCell,
    //   meta: {
    //     filterVariant: FilterType.MULTI_SELECT,
    //     filterOptions: Object.values(Currency).map((currency) => ({
    //       label: currency,
    //       value: currency,
    //     })),
    //     displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
    //     displayColor: 'yellow',
    //   },
    // }),
    columnHelper.accessor('costBudgetRfqPM040SourcingSelected', {
      header: () => <span>PM040 Sourcing</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqPM040TechnicalSelected', {
      header: () => <span>PM040 Technical</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqPM100TechnicalSelected', {
      header: () => <span>PM100 Technical</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqSelection', {
      cell: ({ getValue }) => {
        const selection = getValue()
        if (!Array.isArray(selection) || selection.length === 0) {
          return <>(-)</>
        }
        return <TooltipCell text={selection.join(', ')} />
      },

      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          {
            value: 'PM040',
            label: 'PM040',
          },
          {
            value: 'PM040Selected',
            label: 'PM040 Selected',
          },
          {
            value: 'PM0100',
            label: 'PM0100',
          },
          {
            value: 'PM0100Selected',
            label: 'PM0100 Selected',
          },
          {
            value: 'PR',
            label: 'PR',
          },
        ],
        displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    // columnHelper.accessor('costBudgetRfqTenderDocumentIndex', {
    //   header: () => <span>Tender Doc Version</span>,
    //   cell: ({ getValue }) => <TooltipCell text={getValue() === 0 ? 'Tender' : 'Addendum ' + getValue()} />,

    //   meta: {
    //     filterVariant: FilterType.SELECT_YES_NO,
    //     displayLevel: MySourcingOfferDisplayLevel.COST_BUDGET_RFQ,
    //     displayColor: 'yellow',
    //   },
    // }),
  ]

  const prColumns = [
    columnHelper.accessor('prVendor', {
      header: () => <span>Vendor</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prPRNumber', {
      header: () => <span>PR Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prRaiser', {
      header: () => <span>Raiser</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prRequestor', {
      header: () => <span>Requestor</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prRequestDate', {
      header: () => <span>Request Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prTotalAmount', {
      header: () => <span>Total Amount</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prDescriptionOfProduct', {
      header: () => <span>Description of Product / Service</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prExpectedDeliveryDate', {
      header: () => <span>Expected Delivery Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prDeliveryInstructions', {
      header: () => <span>Delivery Instructions</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prStatus', {
      header: () => <span>Status</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(PRStatus).map((status) => ({ value: status, label: status })),
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prApprovalDueDate', {
      header: () => <span>Approval Due Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prNextApprover', {
      header: () => <span>Next Approver</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLicenseRequired', {
      header: () => <span>License Required</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prApprovedDate', {
      header: () => <span>Approved Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSubmittedDate', {
      header: () => <span>Submitted Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLastApprovalDate', {
      header: () => <span>Last Approval Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prPONumber', {
      header: () => <span>PO #</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSecurityOfPayment', {
      header: () => <span>Security Of Payment</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSubcontractorAgreementRequired', {
      header: () => <span>Subcontractor Agreement Required</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSubcontractorAgreementFileAttached', {
      header: () => <span>Subcontractor Agreement Attached</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prInternalDocumentAttached', {
      header: () => <span>Internal Document Attached</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSAPNumber', {
      header: () => <span>SAP Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prCurrency', {
      header: () => <span>Currency</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(Currency).map((currency) => ({ value: currency, label: currency })),
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prRequiredWorkInstructions', {
      header: () => <span>Required Work Instructions</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prWorkInstructionFileAttached', {
      header: () => <span>Work Instruction File Attached</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MySourcingOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
  ]

  const prLineItemColumns = [
    columnHelper.accessor('prLineItemVendor', {
      header: () => <span>Vendor</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemSubSystemName', {
      header: () => <span>Sub System</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemScopeOfWork', {
      header: () => <span>Scope Of Work</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemProductDescription', {
      header: () => <span>Product Description</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemDescription', {
      header: () => <span>Description</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemBrand', {
      header: () => <span>Brand</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemModelNumber', {
      header: () => <span>Model Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemSAPMaterialNumber', {
      header: () => <span>SAP Material Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemQuantity', {
      header: () => <span>Quantity</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemUnit', {
      header: () => <span>Unit</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          ...new Set([
            ...Object.values(OfferQuotationDetailInstallAndSupplyUnit),
            ...Object.values(OfferQuotationDetailMaintenanceUnit),
          ]),
        ].map((value) => ({ value, label: value })),
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemUnitRate', {
      header: () => <span>Unit Rate</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemDiscount', {
      header: () => <span>Discount (%)</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemTotalPrice', {
      header: () => <span>Total Price</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemCountry', {
      header: () => <span>Country</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemAL', {
      header: () => <span>AL</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemECCN', {
      header: () => <span>ECCN</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemHSCode', {
      header: () => <span>HS Code</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemStartDate', {
      header: () => <span>Start Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemEndDate', {
      header: () => <span>End Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemSiteLocation', {
      header: () => <span>Site Location</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemNumberOfWorker', {
      header: () => <span>No. Of Worker</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemSystem', {
      header: () => <span>System</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemCategory', {
      header: () => <span>Category</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('prLineItemLumpSumDiscount', {
      header: () => <span>Lump Sum Discount</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MySourcingOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
  ]

  if (displayLevel === MySourcingOfferDisplayLevel.COST_BUDGET_RFQ) {
    return [...baseColumns, ...costBudgetRfqColumns]
  }
  if (displayLevel === MySourcingOfferDisplayLevel.PR) {
    return [...baseColumns, ...prColumns]
  }
  if (displayLevel === MySourcingOfferDisplayLevel.PR_LINE_ITEM) {
    return [...baseColumns, ...prColumns, ...prLineItemColumns]
  }
  return [...baseColumns]
}

export const mySourcingCostBudgetColumnAccessors = [
  'costBudgetRfqNumber',
  'costBudgetSubSystemName',
  'costBudgetScopeOfSupply',
  'sourcingDueDate',
  'procurementResponsible',
  'costBudgetProcurementCommented',
  'costBudgetNumberOfContractorPosted',
  'costBudgetNumberOfUpload',
  'costBudgetPM040SourcingSelection',
  'costBudgetPM040TechnicalSelection',
  'costBudgetPM100TechnicalSelection',
  'costBudgetQuoteAmount',
  'costBudgetNoticedProcurementOfficer',
  'costBudgetRequireProcurementOfficer',
  'costBudgetInternalRemark',
  'costBudgetLatestQuoteCurrency',
  'costBudgetLargerThanThreshold',
  'costBudgetWorkflowStatus',
  'costBudgetAddendumStatus',
  'costBudgetCTG',
  'costBudgetCNI',
  'costBudgetAppliedSingleSource',
]

export const mySourcingCostBudgetRfqColumnAccessors = [
  'costBudgetRfqVendorName',
  'costBudgetRfqQuotationUploader',
  'costBudgetRfqQuotationAmount',
  'costBudgetRfqValidDate',
  'costBudgetRfqSubmitDate',
  'costBudgetRfqCurrency',
  'costBudgetRfqPM040SourcingSelected',
  'costBudgetRfqPM040TechnicalSelected',
  'costBudgetRfqPM100TechnicalSelected',
  'costBudgetRfqSelection',
  'costBudgetRfqTenderDocumentIndex',
]

export const mySourcingPRColumnAccessors = [
  'prVendor',
  'prPRNumber',
  'prRaiser',
  'prRequestor',
  'prRequestDate',
  'prTotalAmount',
  'prDescriptionOfProduct',
  'prExpectedDeliveryDate',
  'prDeliveryInstructions',
  'prStatus',
  'prApprovalDueDate',
  'prNextApprover',
  'prLicenseRequired',
  'prApprovedDate',
  'prSubmittedDate',
  'prLastApprovalDate',
  'prPONumber',
  'prSecurityOfPayment',
  'prSubcontractorAgreementRequired',
  'prSubcontractorAgreementFileAttached',
  'prInternalDocumentAttached',
  'prSAPNumber',
  'prCurrency',
  'prRequiredWorkInstructions',
  'prWorkInstructionFileAttached',
]

export const mySourcingPRLineItemColumnAccessors = [
  'prLineItemVendor',
  'prLineItemSubSystemName',
  'prLineItemScopeOfWork',
  'prLineItemProductDescription',
  'prLineItemDescription',
  'prLineItemBrand',
  'prLineItemModelNumber',
  'prLineItemSAPMaterialNumber',
  'prLineItemQuantity',
  'prLineItemUnit',
  'prLineItemUnitRate',
  'prLineItemDiscount',
  'prLineItemTotalPrice',
  'prLineItemCountry',
  'prLineItemAL',
  'prLineItemECCN',
  'prLineItemHSCode',
  'prLineItemStartDate',
  'prLineItemEndDate',
  'prLineItemSiteLocation',
  'prLineItemNumberOfWorker',
  'prLineItemSystem',
  'prLineItemCategory',
  'prLineItemLumpSumDiscount',
]
